import NewDishSizeModal from '@/components/modals/dish-management/dish-size.modal';
import { useModal } from '@/hooks/modal.hook';
import { PlusOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import { FormInstance, Tabs, Typography, Button } from 'antd';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { CreateDishSizeSection } from './size-section.form';
import {
  watchListDishSalt,
  getListDishSalt,
} from '@/redux/slices/dish.slice';
import { useAppSelector, useAppDispatch } from '@/redux/hooks';

type Size = {
  sizeType: string;
  sizeId?: string;
  image?: string;
  dataBig: {
    keyFormula: number;
    id?: string;
    formulaType?: string;
    data: {
      keyComponent: number,
      id?: string;
      ingredient: { name: string, label: string },
      weight: { name: string },
      processingMethod: { name: string },
    }[]
  }[]
}
type ItemTab = {
  key: string;
  label: string;
  sizeType: string;
  closable: boolean,
  children: React.ReactNode;
}
export const CreateDishComponentSection = ({
  sizeRecordData,
  setSizes,
  listSizeDish,
  form,
  onAddSize,
  setRemovedSizeIds,
  existingSizes
}: {
  sizeRecordData?: Size[];
  setSizes: React.Dispatch<React.SetStateAction<Size[]>>;
  listSizeDish?: SizeDish[],
  form?: FormInstance<any>,
  onAddSize?: () => void,
  setRemovedSizeIds?: React.Dispatch<React.SetStateAction<string[]>>,
  existingSizes?: ListSizeItem[]
}) => {
  const [activeKey, setActiveKey] = useState('1');
  const [items, setItems] = useState<ItemTab[]>([]);
  const [isNoSize, setIsNoSize] = useState(false);
  const { openModal, closeModal } = useModal();
  const listSaltDish = useAppSelector(watchListDishSalt);
  const dispatch = useAppDispatch();
  const itemsRef = useRef<ItemTab[]>([]);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    dispatch(getListDishSalt());
  }, [dispatch]);

  const checkSizeDish = (id: string) => {
    const size = listSizeDish?.find(s => s.id === id);
    return size ? size.name : '';
  }

  const onValuesChange = useCallback((
    sizeType: string,
    data: any,
    keyFormula: number,
    keyComponent: number,
    formulaId?: string,
    componentId?: string) => {
    setSizes(prev => {
      const newSizes = [...prev];
      
      const size = newSizes.find(s => s.sizeType === sizeType);

      if (!size) {
        newSizes.push({
          sizeType,
          dataBig: [{
            keyFormula,
            id: formulaId,
            data: [{
              keyComponent,
              id: componentId,
              ...data
            }]
          }]
        });
        return newSizes;
      }

      const dataBig = size.dataBig.find(d => d.keyFormula === keyFormula);

      if (!dataBig) {
        size.dataBig.push({
          keyFormula,
          id: formulaId,
          data: [{
            keyComponent,
            id: componentId,
            ...data
          }]
        });
        return newSizes;
      }

      const component = dataBig.data.find(c => c.keyComponent === keyComponent);
      if (!component) {
        dataBig.data.push({
          keyComponent,
          id: componentId,
          ...data
        });
        return newSizes;
      }
      ['ingredient', 'weight', 'processingMethod'].forEach(key => {
        if (data[key] !== undefined) {
          (component as any)[key] = data[key];
        }
      });

      return newSizes;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onImageChange = useCallback((sizeType: string, image: string) => {
    setSizes(prev => {
      const newSizes = [...prev];
      const size = newSizes.find(s => s.sizeType === sizeType);
      if (size) {
        size.image = image;
      } else {
        newSizes.push({
          sizeType,
          image,
          dataBig: []
        });
      }
      return newSizes;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemoveSize = useCallback((sizeTypeToRemove: string) => {
    if (sizeTypeToRemove === 'NON') {
      setIsNoSize(false);
    }

    const removedSize = sizeRecordData?.find(s => s.sizeType === sizeTypeToRemove);
    if (removedSize?.sizeId && setRemovedSizeIds) {
      const sizeIdToRemove = removedSize.sizeId
      setRemovedSizeIds(prev => [...prev, sizeIdToRemove]);
    }

    setSizes(prevSizes => prevSizes.filter(size => size.sizeType !== sizeTypeToRemove));
    setItems(prev => [
      ...prev.filter(item => item.sizeType !== sizeTypeToRemove),
    ]);
    const keys = items.map(i => Number(i.key));
    if (keys.length > 1) {
      const maxKey = Math.max(...keys);
      setActiveKey(String(maxKey - 1));
    } else {
      setActiveKey('1');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setItems, setSizes, setActiveKey, sizeRecordData, setRemovedSizeIds]);

  const onRemoveValue = useCallback((
    sizeType: string,
    keyFormula: number,
    keyComponent: number
  ) => {
    setSizes(prev => {
      const newSizes = [...prev];
      const sizeIndex = newSizes.findIndex(s => s.sizeType === sizeType);
      if (sizeIndex === -1) return newSizes;

      const size = newSizes[sizeIndex];
      const formulaIndex = size.dataBig.findIndex(f => f.keyFormula === keyFormula);
      if (formulaIndex === -1) return newSizes;

      const formula = size.dataBig[formulaIndex];

      const componentIndex = formula.data.findIndex(c => c.keyComponent === keyComponent);
      if (componentIndex === -1) return newSizes;
      formula.data.splice(componentIndex, 1);

      formula.data = formula.data.map((c, idx) => ({
        ...c,
        keyComponent: idx
      }));

      if (formula.data.length === 0) {
        size.dataBig.splice(formulaIndex, 1);
      }

      if (size.dataBig.length === 0) {
        newSizes.splice(sizeIndex, 1);
      }
      return newSizes;
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRemoveSaltFormula = useCallback((sizeType: string, saltIndex: number) => {
    setSizes(prev => {
      const newSizes = [...prev];
      const size = newSizes.find(s => s.sizeType === sizeType);

      if (!size) return newSizes;

      size.dataBig = size.dataBig.filter(formula => formula.keyFormula !== saltIndex);

      return newSizes;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addTab = ({ label, value }: { label: string; value: string }) => {
    if (label === 'Không chia cỡ' || value === 'NON') {
      setIsNoSize(true);
    }

    const maxKey = Math.max(...(items || []).map(item => parseInt(item.key) || 0), 0);
    const newKey = String(maxKey + 1);

    setActiveKey(newKey);
    setSizes(prev => [...prev, {
      sizeType: value, dataBig: [
        {
          keyFormula: 0,
          data: [
            {
              keyComponent: 0,
              ingredient: { name: "", label: "" },
              weight: { name: "" },
              processingMethod: { name: "" },
            }
          ]
        }
      ]
    }])

    setItems(prev => [
      ...prev,
      {
        label: label,
        key: newKey,
        sizeType: value,
        closable: false,
        children: (
          <CreateDishSizeSection
            size={label}
            key={value}
            sizeType={value}
            onEdit={onEdit}
            onValuesChange={onValuesChange}
            onRemoveValue={onRemoveValue}
            onImageChange={onImageChange}
            handleRemoveSize={handleRemoveSize}
            onRemoveSaltFormula={onRemoveSaltFormula}
            form={form}
            listSaltDish={listSaltDish}
          // sizeRecordData={sizeRecordData}
          />
        )
      }
    ]);



    if (onAddSize) onAddSize();
  };

  const remove = (targetKey: any) => {
    if (!items) return;

    const targetItem = items.find(item => item.key === targetKey);
    if (!targetItem) return;

    const targetIndex = items.findIndex((item) => item.key === targetKey);
    const newItems = items.filter((item) => item.key !== targetKey);

    if (newItems.length && targetKey === activeKey) {
      const newActiveKey =
        newItems[
          targetIndex === newItems.length ? targetIndex - 1 : targetIndex
        ].key;
      setActiveKey(newActiveKey);
    }
    handleRemoveSize(targetItem.sizeType);
    setItems(newItems);
  };

  const changeTabSize = (targetKey: string, label: string, newSize: string, sizeId?: string) => {
    if (!items) return;

    setItems(prev => {
      if (!prev) return [];

      return prev.map(item => {
        if (item.sizeType === targetKey) {
          if (targetKey === newSize) {
            setActiveKey(item.key);
            return item;
          }

          if (newSize === 'NON') {
            setIsNoSize(true);
          } else if (targetKey === 'NON') {
            setIsNoSize(false);
          }

          setSizes(pre => {
            const dataSize = pre.map(it => {
              if (it.sizeType === item.sizeType) {
                return {
                  ...it,
                  sizeType: newSize,
                  sizeId: sizeId
                }
              }

              return it;
            })
            const fieldValues: Record<string, any> = {};
            dataSize.forEach((sizeItem) => {
              sizeItem.dataBig.forEach((formula, formulaIndex) => {
                formula.data.forEach((ingredientItem, componentIndex) => {
                  const name = ingredientItem.ingredient?.name ?? '';
                  const weight = ingredientItem.weight?.name ?? '';
                  const preprocess = ingredientItem.processingMethod?.name ?? '';

                  fieldValues[`size_${newSize}`] = fieldValues[`size_${newSize}`] || {};
                  fieldValues[`size_${newSize}`][`salt_${formulaIndex}`] = fieldValues[`size_${newSize}`][`salt_${formulaIndex}`] || {};
                  fieldValues[`size_${newSize}`][`salt_${formulaIndex}`][`component_${componentIndex}`] = {
                    name,
                    weight,
                    preprocess
                  };
                });
              });
            });

            return dataSize;
          });

          const newItem = {
            ...item,
            key: item.key,
            label: checkSizeDish(newSize),
            sizeType: newSize,
            children: React.cloneElement(
              item.children as React.ReactElement,
              {
                size: label,
                sizeType: newSize,
              }
            )
          };

          setActiveKey(item.key);
          return newItem;
        }
        return item;
      });
    });
  }

  const onEdit = useCallback((targetKey: any, action: 'add' | 'remove' | 'update') => {
    if (action === 'add') {
      openModal(
        <NewDishSizeModal
          size={targetKey}
          callback={addTab}
          onCancel={closeModal}
          items={itemsRef.current}
          existingSizes={existingSizes}
        />
      );
    } else if (action === 'update') {
      openModal(
        <NewDishSizeModal
          size={targetKey}
          changeSize={changeTabSize}
          onCancel={closeModal}
          items={itemsRef.current}
          existingSizes={existingSizes}
        />
      );
    }
    else {
      remove(targetKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addTab, closeModal, changeTabSize, remove, activeKey, existingSizes]);

  useEffect(() => {
    if (sizeRecordData) {
      setItems(
        sizeRecordData.map((size, index) => ({
          key: String(index + 1),
          label: checkSizeDish(size?.sizeType),
          sizeType: size?.sizeType ?? '',
          closable: false,
          forceRender: true,
          children: (
            <CreateDishSizeSection
              key={size.sizeType ?? ''}
              size={checkSizeDish(size?.sizeType)}
              sizeType={size?.sizeType ?? ''}
              onEdit={onEdit}
              onValuesChange={onValuesChange}
              onRemoveValue={onRemoveValue}
              onImageChange={onImageChange}
              handleRemoveSize={handleRemoveSize}
              onRemoveSaltFormula={onRemoveSaltFormula}
              form={form}
              listSaltDish={listSaltDish}
              sizeRecordData={sizeRecordData}
            />
          ),
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form,
    onValuesChange,
    onRemoveValue,
    onImageChange,
    handleRemoveSize,
    listSaltDish,
    sizeRecordData,
  ]);

  return (
    <div css={rootStyles}>
      <Typography.Title level={5}>Thành phần món ăn</Typography.Title>

      <Tabs
        type='editable-card'
        size={'small'}
        activeKey={activeKey}
        onChange={setActiveKey}
        onEdit={(targetKey, action) => onEdit(targetKey, action)}
        items={items}
        className={isNoSize ? 'tab-disabled' : ''}
        addIcon={
          <Button type="primary" css={customButton} icon={<PlusOutlined />} disabled={isNoSize}>
            Thêm cỡ món
          </Button>
        }
      />
    </div>
  );
};

const rootStyles = css`
  width: 100%;
  &.tab-disabled .ant-tabs-nav-add,
  .tab-disabled .ant-tabs-nav-add {
    cursor: not-allowed !important;
    opacity: 0.65;
    color: white !important;
  }

  .ant-tabs-nav-add {
    background-color: var(--menu-active-color) !important;
    color: white !important;
  }
`;

const customButton = css`
  background: none !important;
  border: none;
  &:disabled {
    color: white !important;
  }
`;
